import { Box, Breadcrumbs, Button, Card, Chip, CircularProgress, FormControl, FormLabel, Grid, Input, Stack, Typography } from "@mui/joy";

import {
    PeopleRounded, 
    ChatBubbleRounded, 
    WarningRounded, 
    BlockRounded, 
    AccessTimeRounded, 
    PsychologyRounded, 
    TrendingUpRounded, 
    TrendingDownRounded, 
    RefreshRounded } from '@mui/icons-material';

import { Link, useNavigate } from "react-router";
import { useContext, useEffect } from "react";
import { LayoutContext } from "../context";
import PushNotificationButton from '../components/PushNotificationButton';



const StatCard = ({ title, value, icon, trend, subtitle, color = 'primary' }) => {
    const getTrendIcon = () => {
        if (trend > 0) return <TrendingUpRounded color="success" />;
        if (trend < 0) return <TrendingDownRounded color="danger" />;
        return null;
    };

    const getTrendColor = () => {
        if (trend > 0) return 'success';
        if (trend < 0) return 'danger';
        return 'neutral';
    };

    return (
        <Card variant="outlined" sx={{ height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography level="h2" fontSize="xl" sx={{ mb: 0.5 }}>
                        {value}
                    </Typography>
                    <Typography level="body2" sx={{ mb: 1 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography level="body3" color="neutral">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    backgroundColor: `${color}.100`,
                    color: `${color}.500`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {icon}
                </Box>
            </Box>
            {trend !== undefined && trend !== 0 && (
                <Chip
                    variant="soft"
                    size="sm"
                    color={getTrendColor()}
                    startDecorator={getTrendIcon()}
                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                >
                    {Math.abs(trend)}%
                </Chip>
            )}
        </Card>
    );
};


export default function MainPage() {
    const { dashboardStats, statsLoading, loadDashboardStats, currentDoctor } = useContext(LayoutContext);

    useEffect(() => {
        loadDashboardStats();
    }, []);

    if (statsLoading || !dashboardStats) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '50vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    const {
        total_patients = 0,
        active_chats = 0,
        new_patients_week = 0,
        total_danger_messages = 0,
        recent_danger_messages = 0,
        my_patients = 0,
        my_active_chats = 0,
        my_unread_messages = 0,
        blocked_users = 0,
        expiring_access = 0,
        total_doctors = 0,
        messages_today = 0,
        activity_trend = 0,
        user_type
    } = dashboardStats;

    // user_type на бэке — массив выбранных ролей (MultiSelectField), не строка.
    // Поддерживаем оба варианта на случай старого формата.
    const hasType = (t) => Array.isArray(user_type) ? user_type.includes(t) : user_type === t;
    const isDoctor = hasType('doctor');
    const isModerator = hasType('moderator');
    const isAdmin = hasType('admin') || hasType('supervisor');
    

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: 2,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    // height: '95.5dvh',
                    gap: 1,
                }}
            >
                
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Главная
                    </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <PushNotificationButton />
                </Box>


                {/* Основная статистика */}
                <Grid container spacing={1}>
                    <Grid xs={12} sm={6} md={4} sx={{ height: "fit-content" }}>
                        <StatCard
                            title="Всего пациентов"
                            value={total_patients}
                            icon={<PeopleRounded />}
                            subtitle="Активные пользователи"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4} sx={{ height: "fit-content" }}>
                        <StatCard
                            title="Новые пациенты"
                            value={new_patients_week}
                            icon={<AccessTimeRounded />}
                            subtitle="За 7 дней"
                            color="success"
                        />
                    </Grid>
                    <Grid xs={12} sm={6} md={4} sx={{ height: "fit-content" }}>
                        <StatCard
                            title="Опасные сообщения"
                            value={total_danger_messages}
                            icon={<WarningRounded />}
                            subtitle={`${recent_danger_messages} новых • смотреть статистику в разделе «Чаты»`}
                            color="danger"
                        />
                    </Grid>
                </Grid>

                {/* Статистика для врачей */}
                {isDoctor && (
                    <>
                        <Typography level="h2" fontSize="lg" sx={{ mt: 2 }}>
                            Моя статистика
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid xs={12} sm={6} sx={{ height: "fit-content" }}>
                                <StatCard
                                    title="Мои пациенты"
                                    value={my_patients}
                                    icon={<PeopleRounded />}
                                    color="primary"
                                />
                            </Grid>
                            <Grid xs={12} sm={6} sx={{ height: "fit-content" }}>
                                <StatCard
                                    title="Непрочитанные"
                                    value={my_unread_messages}
                                    icon={<PsychologyRounded />}
                                    color="warning"
                                    subtitle="Подробнее — в разделе «Чаты»"
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Статистика для модераторов и админов */}
                {(isModerator || isAdmin) && (
                    <>
                        <Typography level="h2" fontSize="lg" sx={{ mb: 2 }}>
                            Администрирование
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid xs={12} sm={6} md={6}>
                                <StatCard
                                    title="Заблокировано"
                                    value={blocked_users}
                                    icon={<BlockRounded />}
                                    color="danger"
                                />
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <StatCard
                                    title="Всего врачей"
                                    value={total_doctors}
                                    icon={<PeopleRounded />}
                                    color="neutral"
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Блок «Активность сегодня» вынесен в раздел "Статистика чатов" (/chat-stats) */}
            </Box>
        </Box>
    );
}